import { apiClient } from './client';
import type {
  RiskScore,
  PortfolioSummary,
  CapitalAllocation,
  SiteWithScore,
} from '@/types';

export interface ScoreSiteResponse {
  site_id: string;
  threat_tier: number;
  threat_score: number;
  scored_at: string;
  task_id?: string;
}

export interface OptimizeAllocationRequest {
  total_budget: number;
  fiscal_year: number;
  max_sites?: number;
  min_allocation?: number;
  priority_tiers?: number[];
}

export const riskApi = {
  // Score a single site (triggers refresh)
  scoreSite: async (siteId: string): Promise<ScoreSiteResponse> => {
    const response = await apiClient.post<ScoreSiteResponse>(`/risk/scores/refresh/${siteId}`);
    return response.data;
  },

  // Score multiple sites
  scoreSitesBatch: async (siteIds: string[]): Promise<{ task_id: string; sites_queued: number }> => {
    const response = await apiClient.post('/risk/scores/batch', { site_ids: siteIds });
    return response.data;
  },

  // Get site risk history
  getSiteRiskHistory: async (siteId: string, limit?: number): Promise<RiskScore[]> => {
    const response = await apiClient.get<RiskScore[]>(`/risk/scores/${siteId}/history`, {
      params: { limit },
    });
    return response.data;
  },

  // Get portfolio summary
  getPortfolioSummary: async (): Promise<PortfolioSummary> => {
    const response = await apiClient.get<PortfolioSummary>('/risk/portfolio');
    return response.data;
  },

  // Get ranked sites
  getRankedSites: async (
    limit?: number,
    minTier?: number,
    maxTier?: number
  ): Promise<SiteWithScore[]> => {
    const response = await apiClient.get<{ rankings: SiteWithScore[]; total: number }>('/risk/portfolio/rankings', {
      params: { limit, min_tier: minTier, max_tier: maxTier },
    });
    return response.data.rankings;
  },

  // Optimize capital allocation
  optimizeAllocation: async (params: OptimizeAllocationRequest): Promise<CapitalAllocation> => {
    const response = await apiClient.post<CapitalAllocation>('/risk/allocation/optimize', params);
    return response.data;
  },

  // Get model info
  getModelInfo: async (): Promise<{
    version: string;
    feature_count: number;
    is_trained: boolean;
    validation_auc?: number;
    client_model?: {
      has_trained_model: boolean;
      model_version: string | null;
      training_date: string | null;
      training_samples: number;
      validation_accuracy: number | null;
    };
  }> => {
    const response = await apiClient.get('/risk/model/info');
    return response.data;
  },

  // Train model
  trainModel: async (params?: {
    min_sites?: number;
    validation_split?: number;
    version_note?: string;
  }): Promise<TrainingResponse> => {
    const response = await apiClient.post<TrainingResponse>('/risk/model/train', params || {});
    return response.data;
  },

  // Get model versions
  getModelVersions: async (): Promise<{
    client_id: string;
    total_versions: number;
    versions: ModelVersionInfo[];
  }> => {
    const response = await apiClient.get('/risk/model/versions');
    return response.data;
  },
};

// Training response types
export interface FeatureImportance {
  feature: string;
  importance: number;
}

export interface ConfusionMatrixData {
  matrix: number[][];
  labels: number[];
  true_negatives?: number;
  false_positives?: number;
  false_negatives?: number;
  true_positives?: number;
  precision?: number;
  recall?: number;
}

export interface TrainingResponse {
  success: boolean;
  model_version: string;
  training_samples: number;
  validation_metrics: {
    physical_auc?: number;
    physical_accuracy?: number;
    regulatory_auc?: number;
    regulatory_accuracy?: number;
    financial_rmse?: number;
    timeline_rmse?: number;
    meta_accuracy?: number;
  };
  model_path: string;
  message: string;
  feature_importance?: {
    physical_risk: FeatureImportance[];
    regulatory_risk: FeatureImportance[];
    financial_risk: FeatureImportance[];
    timeline_risk: FeatureImportance[];
  };
  confusion_matrices?: {
    physical_risk?: ConfusionMatrixData;
    regulatory_risk?: ConfusionMatrixData;
    threat_tier?: ConfusionMatrixData;
  };
  data_summary?: {
    total_samples: number;
    physical_risk_distribution: Record<string, number>;
    regulatory_risk_distribution: Record<string, number>;
    threat_tier_distribution: Record<string, number>;
  };
}

export interface ModelVersionInfo {
  version: string;
  model_type: string;
  training_date: string;
  training_samples: number;
  validation_accuracy: number | null;
  validation_auc: number | null;
  is_active: boolean;
  deployed_at: string | null;
  notes: string | null;
}
